import React, { useEffect, useRef } from 'react';
import useGoogleMaps from './useGoogleMaps';

const API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

// Renders the optimized route as numbered markers connected by a polyline.
// `stops` items are expected to expose `coordinates: { latitude, longitude }`
// either at the top level (when normalised) or under pickup/delivery.
export default function OptimizedRouteMap({ stops }) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const overlaysRef = useRef([]); // markers + polyline so we can clean up
  const { ready, error } = useGoogleMaps(API_KEY);

  // Initialise the map once Google Maps script is ready.
  useEffect(() => {
    if (!ready || !containerRef.current || mapRef.current) return;
    const g = window.google.maps;
    mapRef.current = new g.Map(containerRef.current, {
      center: { lat: 0, lng: 0 },
      zoom: 2,
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: false,
    });
  }, [ready]);

  // Re-render markers + polyline whenever the stop list changes.
  useEffect(() => {
    if (!ready || !mapRef.current) return;
    const g = window.google.maps;

    // Tear down previous overlays
    overlaysRef.current.forEach((o) => o.setMap(null));
    overlaysRef.current = [];

    const points = (stops || [])
      .map((s, i) => {
        const coords =
          s.coordinates ||
          s.pickup?.coordinates ||
          s.delivery?.coordinates ||
          null;
        if (!coords?.latitude || !coords?.longitude) return null;
        return {
          index: i,
          lat: coords.latitude,
          lng: coords.longitude,
          label: `${i + 1}`,
          title: s.address || s.pickup?.address || s.delivery?.address || `Stop ${i + 1}`,
          stopType: s.stopType,
        };
      })
      .filter(Boolean);

    if (points.length === 0) {
      mapRef.current.setCenter({ lat: 0, lng: 0 });
      mapRef.current.setZoom(2);
      return;
    }

    const bounds = new g.LatLngBounds();
    points.forEach((p) => {
      const marker = new g.Marker({
        position: { lat: p.lat, lng: p.lng },
        map: mapRef.current,
        label: { text: p.label, color: '#fff', fontWeight: 'bold' },
        title: `${p.label}. ${p.title}`,
      });
      overlaysRef.current.push(marker);
      bounds.extend(marker.getPosition());
    });

    if (points.length > 1) {
      const polyline = new g.Polyline({
        path: points.map((p) => ({ lat: p.lat, lng: p.lng })),
        strokeColor: '#f4b400',
        strokeOpacity: 0.9,
        strokeWeight: 4,
        map: mapRef.current,
      });
      overlaysRef.current.push(polyline);
    }

    mapRef.current.fitBounds(bounds, 60);
    if (points.length === 1) mapRef.current.setZoom(14);
  }, [ready, stops]);

  if (error) {
    return (
      <div className="mq-route-map-error">
        Could not load Google Maps: {error.message}
      </div>
    );
  }

  return (
    <div className="mq-route-map-wrap">
      {!ready && <div className="mq-route-map-loading">Loading map…</div>}
      <div ref={containerRef} className="mq-route-map" />
    </div>
  );
}
